import { apiConnection } from "@/services/api-connection";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { stringManager } from "@/services/utilities/string-manager";
import { messengerMessageManager } from "@/services/messenger-message/messenger-message-manager";

const URL = "/app/studea-manager/training/list";
const URL_TRAINING = "/app/studea-manager/training/";
const URL_TRAINING_COURSES = "/app/studea-manager/training/training-courses/";
const URL_GROUPS = "/app/studea-manager/group/list";
const URL_SIMULATOR_LIST = "/app/studea-manager/training/simulator/list/";
const URL_INSEE_COMPANY = "/app/studea-manager/training/insee-company/";

class TrainingManager {
  list = (params = {}) => {
    return apiConnection.get(URL, params, true).then((response) => response);
  };

  getSimulatorList = (id, params) => {
    return apiConnection.get(URL_SIMULATOR_LIST + id, params).then((response) => response);
  };

  groups = (params = {}) => {
    return apiConnection.get(URL_GROUPS, params, true).then((response) => response);
  };

  form = (type, id = null, search = false, trainingId = null) => {
    return apiConnection
      .get(
        "/app/studea-manager/" +
          (trainingId ? "training/" + trainingId + "/" : "") +
          type +
          "/" +
          (search ? "search/" : "") +
          "form" +
          (id ? "/" + id : ""),
      )
      .then((response) => response);
  };

  get = (id, path, objectId = null) => {
    return apiConnection
      .get(URL_TRAINING + id + "/" + path + (objectId ? "/" + objectId : ""))
      .then((response) => response);
  };

  sort = async (id, trainingId, position, alias, tradPrefix) => {
    tradPrefix = stringManager.toCamelCase(tradPrefix);
    const response = await apiConnection
      .put(URL_TRAINING + trainingId + "/" + alias + "/sort/" + id + "/" + position)
      .then((response) => response);
    if (response) {
      notificationManager.showNotification(
        response.success ? "success" : "error",
        response.success
          ? i18n.global.tc("global.sort.success", {
              name: i18n.global.tc("studea_manager." + tradPrefix + ".title", 2).toLowerCase(),
            })
          : i18n.global.t("signed_error"),
      );
    }
  };

  accept = async (skillId, categoryId, trainingId, alias, tradPrefix) => {
    tradPrefix = stringManager.toCamelCase(tradPrefix);
    const response = await apiConnection
      .put(URL_TRAINING + trainingId + "/" + alias + "/accept/" + skillId + "/category/" + categoryId)
      .then((response) => response);
    if (response) {
      notificationManager.showNotification(
        response.success ? "success" : "error",
        response.success
          ? i18n.global.tc("global.accept.success", 2, {
              name: i18n.global.tc("studea_manager." + tradPrefix + ".title", 1),
            })
          : i18n.global.t("signed_error"),
      );
    }
    return response;
  };

  post = (id, path, params = {}) => {
    const finalSlash =
      Object.keys(params).length > 0 && path !== "program/add-files" && path !== "skill/publish-skills" ? "/" : "";
    return apiConnection
      .post(URL_TRAINING + id + "/" + path + finalSlash, params)
      .then((response) => response)
      .catch((err) => err);
  };

  put = (id, trainingId, path, params = {}) => {
    return apiConnection
      .put(URL_TRAINING + trainingId + "/" + path + (id ? "/" + id : ""), params)
      .then((response) => response)
      .catch((err) => err);
  };

  getTraining = (id) => {
    return apiConnection
      .get(URL_TRAINING + "get/" + id)
      .then((response) => response)
      .catch((err) => err);
  };

  delete = async (alias, id) => {
    return await apiConnection.delete("/app/studea-manager/" + alias + "/delete/" + id);
  };

  trainingCourses = (id) => {
    return apiConnection
      .get(URL_TRAINING_COURSES + id)
      .then((response) => response)
      .catch((err) => err);
  };

  remove = async (id, trainingId, path, alias) => {
    const tradGender = stringManager.gender(alias);
    const tradPrefix = stringManager.toCamelCase(alias);
    const alertResponse = await notificationManager.showAlert(
      "error",
      i18n.global.tc("global.delete.title", tradGender, {
        name: i18n.global.tc("studea_manager." + tradPrefix + ".title", 1).toLowerCase(),
      }),
      i18n.global.tc("global.delete.sub_title", tradGender - 1, {
        name: i18n.global.tc("studea_manager." + tradPrefix + ".title", 1).toLowerCase(),
      }),
      true,
      true,
    );
    const url = URL_TRAINING + trainingId + "/" + path + "/delete/" + id;

    if (!alertResponse.isConfirmed) {
      return false;
    }

    const response = await apiConnection.delete(url);

    if (response) {
      notificationManager.showNotification(
        response.success ? "success" : "error",
        response.success
          ? i18n.global.tc("global.delete.success", tradGender - 1, {
              name: i18n.global.tc("studea_manager." + tradPrefix + ".title", 1),
            })
          : i18n.global.t("signed_error"),
      );
      return response;
    }
  };

  async enrich(trainingId, objectId, type) {
    const url = URL_TRAINING + trainingId + "/" + type + "/archive-" + type + "s/" + objectId;
    return await apiConnection.post(url, { limit: 3 });
  }

  async exportInseeCompany() {
    const response = await apiConnection.get(URL_INSEE_COMPANY + "file/download");
    if (response) {
      messengerMessageManager.processMessage(response);
    }
  }

  async getRegistrationList(trainingId, params) {
    return await apiConnection.get("/app/ufa/training/" + trainingId + "/registration/list", params);
  }

  async getUfaRegistrationSearchForm(trainingId, params = {}) {
    return await apiConnection.get("/app/ufa/training/" + trainingId + "/registration/search-form", params);
  }
}

export const trainingManager = new TrainingManager();
